/* Multi-Location Screenshot Service v2.2 */
(function() {
  'use strict';

  var DB_NAME = 'customizer-mockups';
  var STORE = 'blobs';
  var dbPromise = null;

  // 1x1 transparent PNG used as placeholder when logo/text blobs are null.
  // Prevents n8n "no binary field" errors — the workflow always expects all 3 fields.
  var PLACEHOLDER_PNG = (function() {
    var b64 = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVQI12NgAAIABQAB' +
              'Nl7BcQAAAABJRU5ErkJggg==';
    var bin = atob(b64);
    var arr = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: 'image/png' });
  })();

  // Shared key format so writer (customizer-v2.js) and reader (cart-drawer.liquid)
  // never drift. A typo here would silently break the local-blob fallback.
  function idbKey(kind, customizationKey, locationId) {
    return kind + ':' + customizationKey + ':' + locationId;
  }

  function openDb() {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise(function(resolve, reject) {
      try {
        var r = indexedDB.open(DB_NAME, 1);
        r.onupgradeneeded = function() {
          if (!r.result.objectStoreNames.contains(STORE)) {
            r.result.createObjectStore(STORE);
          }
        };
        r.onsuccess = function() { resolve(r.result); };
        r.onerror = function() { reject(r.error); };
      } catch (e) {
        reject(e);
      }
    });
    return dbPromise;
  }

  function idbPut(key, blob) {
    return openDb().then(function(db) {
      return new Promise(function(resolve, reject) {
        var tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).put(blob, key);
        tx.oncomplete = function() { resolve(true); };
        tx.onerror = function() { reject(tx.error); };
      });
    }).catch(function(err) {
      console.warn('[Screenshot] idbPut failed for', key, err);
      return false;
    });
  }

  function idbGet(key) {
    return openDb().then(function(db) {
      return new Promise(function(resolve) {
        var tx = db.transaction(STORE, 'readonly');
        var g = tx.objectStore(STORE).get(key);
        g.onsuccess = function() { resolve(g.result || null); };
        g.onerror = function() { resolve(null); };
      });
    }).catch(function() { return null; });
  }

  function idbDelete(key) {
    return openDb().then(function(db) {
      return new Promise(function(resolve) {
        var tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).delete(key);
        tx.oncomplete = function() { resolve(true); };
        tx.onerror = function() { resolve(false); };
      });
    }).catch(function() { return false; });
  }

  // Serialize concurrent add-to-cart events so a second click can't race
  // an in-flight capture and read mid-mutation Fabric state.
  var captureChain = Promise.resolve();
  function enqueue(fn) {
    var next = captureChain.then(fn).catch(function(err) {
      console.error('[Screenshot] queued task failed:', err);
    });
    captureChain = next;
    return next;
  }

  // Composites the customizer's product <img> + Fabric overlay into a blob.
  // Reads from the LIVE editor — same pixels the customer just approved —
  // so there's no offscreen reconstruction to drift from the editor view.
  async function captureLiveCanvas(cfg) {
    var productImage = cfg && cfg.productImage;
    var fabricCanvas = cfg && cfg.fabricCanvas;
    var opts = (cfg && cfg.opts) || {};
    var maxDim = opts.maxDimension || 1200;
    var format = opts.format || 'image/jpeg';
    var quality = opts.quality != null ? opts.quality : 0.92;

    if (!productImage || !fabricCanvas) {
      console.warn('[Screenshot] captureLiveCanvas missing inputs');
      return null;
    }
    if (!productImage.complete || !productImage.naturalWidth) {
      console.warn('[Screenshot] product image not ready');
      return null;
    }

    // Wait for fonts before any text-bearing capture
    try { await document.fonts.ready; } catch (e) { /* fonts API unavailable */ }

    // Force a render and wait one paint frame for font rasterization
    fabricCanvas.renderAll();
    await new Promise(function(r) { requestAnimationFrame(r); });

    // Re-render AFTER the RAF — the browser may have discarded the canvas
    // backing store during the yield (e.g. cart drawer opening causes layout
    // shift that invalidates offscreen canvases). This second renderAll()
    // repopulates the backing store from Fabric's internal object list.
    fabricCanvas.renderAll();

    var dispW = productImage.clientWidth || fabricCanvas.width;
    var dispH = productImage.clientHeight || fabricCanvas.height;
    if (!dispW || !dispH) {
      console.warn('[Screenshot] product image has zero dimensions');
      return null;
    }

    // Use the original image resolution (e.g. 2048px) for sharper output,
    // then cap at maxDim to keep file size reasonable for upload.
    var natW = productImage.naturalWidth || dispW;
    var natH = productImage.naturalHeight || dispH;
    var scale = Math.min(1, maxDim / Math.max(natW, natH));
    var outW = Math.round(natW * scale);
    var outH = Math.round(natH * scale);

    var out = document.createElement('canvas');
    out.width = outW;
    out.height = outH;
    var ctx = out.getContext('2d');
    if (!ctx) return null;

    // Draw product image as background
    try {
      ctx.drawImage(productImage, 0, 0, outW, outH);
    } catch (e) {
      console.error('[Screenshot] failed to draw product image (likely CORS taint):', e);
      return null;
    }

    // Draw fabric overlay on top (logo + text)
    try {
      var fabricEl = fabricCanvas.lowerCanvasEl;

      if (fabricEl) ctx.drawImage(fabricEl, 0, 0, outW, outH);
    } catch (e) {
      console.error('[Screenshot] failed to draw fabric overlay (likely CORS taint):', e);
      return null;
    }

    return new Promise(function(resolve) {
      out.toBlob(function(blob) {
        if (!blob) console.warn('[Screenshot] toBlob returned null (memory pressure?)');
        resolve(blob);
      }, format, quality);
    });
  }

  // Captures the logo straight from the live Fabric canvas where
  // renderLogoOnCanvas has already drawn it with the customer's thread colors.
  // Doing it this way means there is exactly one path that applies colors
  // (the editor's renderLogoOnCanvas) — fetching the SVG again here was a
  // recurring source of color drift bugs.
  async function captureLogoFromFabric(fabricCanvas, opts) {
    if (!fabricCanvas) return null;
    opts = opts || {};
    var quality = opts.quality != null ? opts.quality : 0.85;
    var maxDim = opts.maxDimension || 512;

    var logoObj = fabricCanvas.getObjects().find(function(o) { return o.isLogo; });
    if (!logoObj) {
      console.warn('[Screenshot] captureLogoFromFabric: no logo object on canvas');
      return null;
    }

    try { await document.fonts.ready; } catch (e) { /* fonts API unavailable */ }
    fabricCanvas.renderAll();
    await new Promise(function(r) { requestAnimationFrame(r); });

    // withoutTransform: render at the logo's natural size and orientation,
    // ignoring its placement/rotation/scale on the product. The cart drawer
    // thumbnail is a 60x60 icon — we just need the design, not the placement.
    var natW = logoObj.width || maxDim;
    var natH = logoObj.height || maxDim;
    var multiplier = Math.min(maxDim / natW, maxDim / natH, 2);
    if (!isFinite(multiplier) || multiplier <= 0) multiplier = 1;

    var dataUrl;
    try {
      dataUrl = logoObj.toDataURL({
        format: 'jpeg',
        quality: quality,
        multiplier: multiplier,
        withoutTransform: true,
        withoutShadow: true
      });
    } catch (e) {
      console.error('[Screenshot] captureLogoFromFabric toDataURL failed (CORS taint?):', e);
      return null;
    }

    try {
      var response = await fetch(dataUrl);
      return await response.blob();
    } catch (e) {
      console.error('[Screenshot] captureLogoFromFabric blob conversion failed:', e);
      return null;
    }
  }

  // Renders just the personalization text to a tight canvas.
  async function captureTextOnly(locState, getDefaultFont, loadFont) {
    if (!locState || !locState.personalization || !locState.personalization.text) return null;
    var personalization = locState.personalization;
    var text = personalization.text.trim();
    if (!text) return null;

    var font = personalization.font || getDefaultFont();
    var threadColor = personalization.threadColor || { hex: '#FFFFFF' };
    var fillColor = threadColor.hex.charAt(0) === '#' ? threadColor.hex : '#' + threadColor.hex;

    try {
      await loadFont(font);
      await document.fonts.ready;
    } catch (e) {
      console.warn('[Screenshot] captureTextOnly font load failed:', e);
    }

    var canvas = document.createElement('canvas');
    var ctx = canvas.getContext('2d');
    var fontSize = (personalization.position && personalization.position.fontSize) || 40;
    ctx.font = fontSize + 'px ' + font.family;
    var metrics = ctx.measureText(text);
    var textHeight = fontSize * 1.2;

    canvas.width = Math.ceil(metrics.width + 40);
    canvas.height = Math.ceil(textHeight + 40);

    ctx.font = fontSize + 'px ' + font.family;
    ctx.fillStyle = fillColor;
    ctx.textAlign = personalization.textAlign || 'center';
    ctx.textBaseline = 'middle';

    var x = personalization.textAlign === 'center' ? canvas.width / 2 :
            personalization.textAlign === 'right' ? canvas.width - 20 : 20;
    var y = canvas.height / 2;
    ctx.fillText(text, x, y);

    return new Promise(function(resolve) {
      canvas.toBlob(function(blob) {
        if (!blob) console.warn('[Screenshot] captureTextOnly toBlob returned null');
        resolve(blob);
      }, 'image/jpeg', 0.85);
    });
  }

  // Uploads captures to n8n with retry. Best-effort: IndexedDB blobs
  // are the local fallback if every attempt fails.
  async function sendToWebhook(captures, options) {
    options = options || {};
    var webhookUrl = options.webhookUrl || window.SCREENSHOT_WEBHOOK_URL;
    var metadata = options.metadata || {};
    var locationUrls = options.locationUrls || {};
    var maxRetries = options.maxRetries || 3;

    if (!webhookUrl) {
      console.error('[Screenshot] No webhook URL configured');
      return null;
    }

    var formData = new FormData();

    captures.forEach(function(capture, index) {
      var locationId = capture.locationId;
      var location = capture.location;
      var locState = capture.locState;
      var blobs = capture.blobs;
      var urlData = locationUrls[locationId] || {};
      var filenames = urlData.filenames || {};

      // Always send all 3 binary fields — n8n expects them. Use placeholder PNG when null.
      formData.append(
        'location_' + index + '_fullMockup',
        blobs.fullMockup || PLACEHOLDER_PNG,
        filenames.mockup || (locationId + '_mockup.jpg')
      );
      formData.append(
        'location_' + index + '_logo',
        blobs.logo || PLACEHOLDER_PNG,
        filenames.logo || (locationId + '_logo.png')
      );
      formData.append(
        'location_' + index + '_text',
        blobs.text || PLACEHOLDER_PNG,
        filenames.text || (locationId + '_text.png')
      );

      formData.append('location_' + index + '_metadata', JSON.stringify({
        locationId: locationId,
        locationName: location && location.name,
        logoId: locState && locState.selectedLogo && locState.selectedLogo.id,
        personalizationText: locState && locState.personalization && locState.personalization.text,
        threadColors: locState && locState.logoThreadColors,
        screenshotProductUrl: filenames.mockup,
        screenshotLogoUrl: filenames.logo,
        screenshotTextUrl: filenames.text
      }));
    });

    formData.append('metadata', JSON.stringify(Object.assign({}, metadata, {
      locationCount: captures.length
    })));

    var lastError = null;
    for (var attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        var controller = new AbortController();
        var timeoutId = setTimeout(function() { controller.abort(); }, 30000);
        var response = await fetch(webhookUrl, {
          method: 'POST',
          body: formData,
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        if (!response.ok) {
          throw new Error('Webhook ' + response.status + ' ' + response.statusText);
        }
        return await response.json();
      } catch (error) {
        lastError = error;
        // Don't retry client errors
        if (/\b(400|401|403)\b/.test(error.message || '')) break;
        if (attempt < maxRetries) {
          var delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          await new Promise(function(r) { setTimeout(r, delay); });
        }
      }
    }
    console.error('[Screenshot] Webhook failed after all retries:',
      lastError && lastError.message, 'for', metadata.customizationKey);
    return null;
  }

  window.MultiLocationScreenshotService = {
    captureLiveCanvas: captureLiveCanvas,
    captureLogoFromFabric: captureLogoFromFabric,
    captureTextOnly: captureTextOnly,
    sendToWebhook: sendToWebhook,
    enqueue: enqueue,
    idbKey: idbKey,
    idbPut: idbPut,
    idbGet: idbGet,
    idbDelete: idbDelete
  };
})();
